// Moved from `src/hooks/mutation/useMutateUpgradePlan.ts` (now a delegate) at
// D2 batch 3, ARCFE-976.

import { useMutation } from '@tanstack/react-query';
import { type Period, StripeFreeTrialDays, type SubscriptionTier } from '../../contracts';
import { changePlan } from '../api/subscription';

export type UseMutateUpgradePlanVariables = {
  plan_period: Period;
  free_trial_days?: StripeFreeTrialDays;
  tier: SubscriptionTier;
};

export type UseMutateUpgradePlanProps = {
  /**
   * `!isNonOwnerTeamMember`, resolved app-side from the workspace door.
   *
   * The legacy hook read the team role itself and, when the member could not
   * upgrade, TOASTED AND RETURNED from inside `mutationFn` — a user-visible
   * effect on the request path. Inverting it to a boolean input plus a port
   * keeps the behaviour identical (still no request, still the same toast in
   * the same place) while leaving both the role read and the toast app-side.
   */
  canUpgrade: boolean;
  /** Fires exactly where the "you can't upgrade" toast fired. No request is made. */
  onUpgradeBlocked?: () => void;
  /**
   * Fires at the top of the legacy `onSuccess`, BEFORE its `sleep(1500)`.
   * That sleep is load-bearing — it waits for the Stripe webhook to reach the
   * backend, so invalidating sooner refetches stale billing — and it stays in
   * the delegate beside the invalidators it sequences.
   */
  onUpgradeSuccess?: () => void | Promise<void>;
  onUpgradeError?: (error: unknown) => void;
};

/**
 * POST /billings/change-plan.
 *
 * Note the return contract is preserved exactly, including its oddity: on the
 * blocked path the legacy `mutationFn` returned `undefined` rather than
 * throwing, so the mutation resolves SUCCESSFULLY with no data and `onSuccess`
 * runs. Callers depend on not seeing an error state there. Converting the
 * guard to a throw would be tidier and would surface an error toast where
 * today there is none.
 */
export function useMutateUpgradePlan({
  canUpgrade,
  onUpgradeBlocked,
  onUpgradeSuccess,
  onUpgradeError,
}: UseMutateUpgradePlanProps) {
  return useMutation({
    mutationKey: ['upgradePlan'],
    mutationFn: async (input: UseMutateUpgradePlanVariables) => {
      if (!canUpgrade) {
        onUpgradeBlocked?.();
        return;
      }

      const res = await changePlan({
        period: input.plan_period,
        tier: input.tier,
        free_trial_days: input.free_trial_days || StripeFreeTrialDays.Value30,
      });
      return res;
    },
    onSuccess: async () => {
      await onUpgradeSuccess?.();
    },
    onError: (error) => {
      onUpgradeError?.(error);
    },
  });
}
